/**
 * @hook usePageMove
 * @title 페이지이동 호출 훅
 * @description
 * @properties
 * @returns {any 반환 형태를 간단히 요약합니다.}
 * @author 2034263
 * @history
 *   - [2026-02-04] 초기 작성
 */

import { useCallback } from 'react';

import {
  Channel,
  deviceUtils,
  Envs,
  MenuOptions,
  useAppBridge,
  useAppContext,
  useCurrentMenu,
  useEnvContext,
  useMenuRouter,
  useSessionService,
  useUserContext,
  useUserData,
  useWebviewBridge,
} from 'scbk-services';
import { Dialog, useLoading } from 'scbk-shared-module';

/**
 * 메인이동 옵션
 */
interface MainOptions extends MenuOptions {
  refresh?: boolean; //메인화면 refresh 여부
}

export const usePageMove = () => {
  const { getEnv } = useEnvContext();
  const { channel, seniorMode } = useAppContext().getContext();
  const { isLoginUser } = useUserContext();
  const { clearUserData } = useUserData();
  const { doNGoMain } = useWebviewBridge();
  const { goMenu } = useMenuRouter();
  const { currentMenuId } = useCurrentMenu();
  const { executeLogout } = useSessionService();

  const { hide: hideLoading } = useLoading();

  const { doNSetWebviewStorage } = useAppBridge();

  /**
   * 로그인 페이지로 이동
   *
   * - isGoPreLogin: boolean // 프리로그인으로 이동이 필요할 경우
   * - menuOptions : {
   *    routeData : {
   *        forceLoginType?: LoginType; // 로그인 실행 타입 지정
   *        nextPageInfo?: LoginNextPageInfo; // 다음페이지정보
   *    }
   * }
   */
  const goLogin = useCallback(
    async ({
      isGoPreLogin = false,
      menuOptions,
    }: {
      isGoPreLogin?: boolean;
      menuOptions?: MenuOptions;
    } = {}) => {
      console.log(
        'userPageMove > goLogin - isGoPreLogin : ',
        isGoPreLogin,
        '| menuOptions : ',
        menuOptions,
      );

      const query = new URLSearchParams(menuOptions?.query);
      query.set('ts', String(Date.now()));

      // 메뉴 옵션 수정
      menuOptions = {
        ...menuOptions,
        query: query,
        historyOptions: { ...menuOptions?.historyOptions, clearHistory: true },
      };

      //모바일웹
      if (channel === Channel.MW) {
        // 모바일웹 로그인 불가
        if (Envs.PROD !== getEnv()) {
          Dialog.alert({ content: '모바일웹 로그인 페이지 이동 불가' });
        }
        return;
      }

      if (isLoginUser()) {
        // 로그인 상태로 호출시 강제 로그아웃 처리
        try {
          await executeLogout();
        } finally {
          await clearUserData({ nativeLogout: true });
        }
      }

      if (channel === Channel.IB) {
        // 인터넷뱅킹 프리로그인
        goMenu('LGNADV000', menuOptions);
        return;
      }

      // 모바일뱅킹 로그인
      if (currentMenuId === 'LGNMTH000') {
        isGoPreLogin = true;
      }

      if (!isGoPreLogin) {
        goMenu('LGNMTH000', menuOptions);
        return;
      }

      // 모바일뱅킹 프리로그인
      if (Envs.PROD === getEnv() || Envs.UAT === getEnv() || deviceUtils.isApp()) {
        goMain({
          type: 'PRE-LOGIN',
          menuOptions,
        });
      } else {
        goMenu('LGNADV000', menuOptions);
      }
    },
    [channel, isLoginUser, goMenu],
  );

  /**
   * 메인화면으로 이동
   */
  const goMain = useCallback(
    async ({
      type = '',
      menuOptions,
    }: {
      type?: 'HOME' | 'INQUIRY' | 'TRANSFER' | 'INVEST' | 'PRODUCT' | 'ALL' | 'PRE-LOGIN' | '';
      menuOptions?: MainOptions;
    } = {}) => {
      console.log(`userPageMove > goMain - type : ${type}, menuOptions : ${menuOptions}`);

      if (channel === Channel.MW) {
        // 모바일웹 로그인 불가로 메인이동 불가
        goMenu('MA3PRDALL001');
        return;
      }

      if (channel === Channel.IB || !deviceUtils.isApp()) {
        // 비로그인 상태
        if (!isLoginUser()) {
          // 프리로그인으로 이동
          goLogin({ isGoPreLogin: true });
          return;
        } else if (type === 'TRANSFER') {
          goMenu('TRNMAN000', { historyOptions: { clearHistory: true } });
        } else {
          goMenu('KLDHOM000', { historyOptions: { clearHistory: true } });
        }
        return;
      }

      if (!isLoginUser() && type != 'PRODUCT') {
        type = 'PRE-LOGIN';
      }

      //***************************************
      // 메인탭 refresh 여부를 로컬스토리지에 담는다.
      // 메인화면에서는 세션스토리지 확인 및 처리 후 로컬스토리지를 clear 한다.
      //***************************************
      const refresh = menuOptions?.refresh ?? false;
      if (refresh) {
        //[2026.08.27 최성돈] 네이티브 웹뷰 스토리지로 대체

        //TODO 메인에서 반영할때까지 삭제하지 않는다.
        localStorage.setItem(`${type}_refresh_yn`, 'Y');
        await doNSetWebviewStorage(`${type}_refresh_yn`, 'Y');
      }

      // 결함 140385 대응 처리
      // 로딩바를 닫는다.
      //loadingManager.close();
      await hideLoading();

      if (type === 'INVEST' && seniorMode === 'Y') {
        // await doNClearNavibarSelection();
        goMenu('MA3ASTMGT001', {
          webviewType: 'v3',
          historyOptions: { clearHistory: true },
        });
        return;
      }

      //메인화면으로 이동
      doNGoMain(type);
    },
    [channel, isLoginUser, goMenu, doNGoMain],
  );

  return {
    goLogin,
    goMain,
  };
};
